import { useState } from "react";
import { Link } from "react-router-dom";
import { Accessibility, ArrowLeft, Check, AlertTriangle, Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const today = new Date().toLocaleDateString("de-DE");

const CONFORM = [
    "Durchgängige Tastaturbedienbarkeit und sichtbarer Tastatur-Fokus (WCAG 2.1.1 / 2.4.7)",
    "„Zum Hauptinhalt springen\"-Sprunglink als erstes fokussierbares Element (2.4.1)",
    "Logische Fokus-Reihenfolge, keine Tastaturfallen, Dialoge mit Fokusfalle & Esc-Schließen (2.4.3 / 2.1.2)",
    "Korrekte Dokumentsprache (lang=\"de\") und semantische Landmarken (header, nav, main)",
    "Beschriftungen/aria-labels für Bedienelemente, Formularfelder und Symbol-Schaltflächen (1.3.1 / 4.1.2)",
    "Ausreichende Farbkontraste (≥ 4,5:1 Text, ≥ 3:1 große Schrift) in hellem und dunklem Modus (1.4.3)",
    "Beachtung der Systemeinstellung „Bewegung reduzieren\" (prefers-reduced-motion) (2.3.3)",
];

const KNOWN_LIMITS = [
    "Von Händlern hochgeladene PDF-Dokumente und nutzergenerierte Inhalte sind ggf. nicht vollständig barrierefrei.",
    "Eine ergänzende manuelle Screenreader-Prüfung (NVDA/VoiceOver) einzelner Spezialansichten ist noch in Arbeit.",
    "Komplexe interaktive Module (z. B. Kalenderraster, Diagramme) werden schrittweise weiter optimiert.",
];

export default function BarrierefreiheitPage() {
    const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim() || !form.email.trim()) {
            toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/public/contact-leads", { ...form, source: "barrierefreiheit" });
            setSent(true);
            toast.success("Vielen Dank – Ihre Rückmeldung ist eingegangen.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Senden fehlgeschlagen. Bitte später erneut versuchen.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="barrierefreiheit-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
                    <Link to="/" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="barrierefreiheit-back">
                        <ArrowLeft className="w-3.5 h-3.5" /> Startseite
                    </Link>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2 mt-4">BFSG · Barrierefreiheit</div>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3">
                        <Accessibility className="w-9 h-9 text-[#FFCC00]" /> Erklärung zur Barrierefreiheit
                    </h1>
                    <p className="text-sm text-gray-400 mt-3">Gemäß Barrierefreiheitsstärkungsgesetz (BFSG) und EU-Richtlinie 2019/882 (European Accessibility Act).</p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 text-gray-800 space-y-8 text-sm leading-relaxed">
                <section>
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Unser Anspruch</h2>
                    <p>TwoWheels ist bemüht, seine Website und Anwendungen im Einklang mit dem Barrierefreiheitsstärkungsgesetz (BFSG) barrierefrei zugänglich zu machen. Zielstandard ist die <strong>WCAG 2.1, Konformitätsstufe AA</strong> (entspricht EN 301 549).</p>
                </section>

                <section>
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Stand der Vereinbarkeit</h2>
                    <p>Diese Website ist mit der WCAG 2.1 Stufe AA <strong>weitgehend vereinbar</strong>. Die zentralen öffentlichen Seiten sowie die geschäftskritischen Arbeitsabläufe (Anmeldung, Fahrzeugbestand, Disposition/Verkauf, Werkstatt, Verwaltung) wurden geprüft und erfüllen die automatisiert prüfbaren Erfolgskriterien. Umgesetzte Maßnahmen u. a.:</p>
                    <ul className="list-none space-y-1.5 pl-0 mt-2">
                        {CONFORM.map((it) => (
                            <li key={it} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> <span>{it}</span></li>
                        ))}
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Nicht barrierefreie Inhalte</h2>
                    <p>Trotz unserer Bemühungen können einzelne Inhalte derzeit eingeschränkt zugänglich sein:</p>
                    <ul className="list-none space-y-1.5 pl-0 mt-2">
                        {KNOWN_LIMITS.map((it) => (
                            <li key={it} className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> <span>{it}</span></li>
                        ))}
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Erstellung dieser Erklärung</h2>
                    <p>Diese Erklärung wurde am <strong>{today}</strong> erstellt bzw. aktualisiert. Grundlage ist eine <strong>Selbstbewertung</strong> kombiniert mit:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                        <li>automatisierten Tests mit <strong>axe-core 4.10</strong> über die zentralen öffentlichen und internen Seiten (0 festgestellte WCAG-2-A/AA-Verstöße),</li>
                        <li>einer <strong>Tastatur-Navigationsprüfung</strong> der Kern-Flows (Fokus-Reihenfolge, Sprunglink, Tastaturfallen, Dialog-Fokusverwaltung),</li>
                        <li>laufender manueller Nachkontrolle einzelner Ansichten.</li>
                    </ul>
                </section>

                <section data-testid="barrierefreiheit-feedback">
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Barrieren melden &amp; Feedback</h2>
                    <p>Sind Ihnen Barrieren aufgefallen oder benötigen Sie Informationen in einem barrierefreien Format? Schreiben Sie uns – wir antworten in der Regel innerhalb weniger Werktage.</p>
                    <p className="mt-1"><strong>E-Mail:</strong> <a className="text-[#1d4ed8] underline" href="mailto:barrierefreiheit@twowheels.info">barrierefreiheit@twowheels.info</a></p>

                    {sent ? (
                        <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-emerald-900" data-testid="barrierefreiheit-feedback-success">
                            <Check className="w-4 h-4 inline mr-1" /> Vielen Dank für Ihre Rückmeldung. Wir kümmern uns darum.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-4 space-y-3 max-w-xl" data-testid="barrierefreiheit-feedback-form">
                            <div>
                                <label htmlFor="bf-name" className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Name</label>
                                <Input id="bf-name" value={form.name} onChange={upd("name")} required data-testid="barrierefreiheit-name" />
                            </div>
                            <div>
                                <label htmlFor="bf-email" className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">E-Mail</label>
                                <Input id="bf-email" type="email" value={form.email} onChange={upd("email")} required data-testid="barrierefreiheit-email" />
                            </div>
                            <div>
                                <label htmlFor="bf-message" className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Ihre Rückmeldung</label>
                                <textarea id="bf-message" value={form.message} onChange={upd("message")} required rows={4}
                                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8300]"
                                    placeholder="Welche Barriere ist Ihnen begegnet? Auf welcher Seite?" data-testid="barrierefreiheit-message" />
                            </div>
                            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={upd("website")} className="hidden" />
                            <Button type="submit" disabled={busy} className="bg-[#FF8300] hover:bg-[#E6760A] text-[#09090B] font-semibold" data-testid="barrierefreiheit-submit">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1.5" /> Rückmeldung senden</>)}
                            </Button>
                        </form>
                    )}
                    <p className="mt-3 text-xs text-gray-500">Weitere Kontaktwege finden Sie im <Link to="/legal/impressum" className="text-[#1d4ed8] underline">Impressum</Link>.</p>
                </section>

                <section>
                    <h2 className="font-display text-2xl font-black text-[#111827] mb-2">Durchsetzungsverfahren</h2>
                    <p>Sind Sie mit unseren Antworten nicht zufrieden, können Sie sich an die zuständige <strong>Marktüberwachungsstelle der Länder für Barrierefreiheit (MLBF)</strong> bzw. die Schlichtungsstelle nach dem BFSG wenden. Informationen hierzu erhalten Sie bei der jeweils zuständigen Landesbehörde.</p>
                </section>

                <p className="text-xs text-gray-500">Diese Erklärung beruht auf einer Selbstbewertung sowie automatisierten und manuellen Tests. Zuletzt aktualisiert am {today}.</p>
            </div>
        </div>
    );
}
